import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, History, ShoppingCart, Wrench, FileDown } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../ui/table";
import { toast } from "sonner";
import api, { eur, formatApiErrorDetail, downloadFile } from "../../lib/api";

const empty = { name: "", type: "", city: "", contact: "", phone: "", email: "", notes: "", discount_pct: 0 };

export default function ClientsPanel() {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [histOpen, setHistOpen] = useState(false);

  const load = () => api.get("/clients").then(({ data }) => setClients(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openForm = (c) => { setForm(c ? { ...c } : empty); setEditId(c?.id || null); setOpen(true); };
  const save = async () => {
    if (!form.name) { toast.error("Inserisci il nome del cliente"); return; }
    try {
      const payload = { name: form.name, type: form.type, city: form.city, contact: form.contact, phone: form.phone, email: form.email, notes: form.notes, discount_pct: Number(form.discount_pct) || 0 };
      if (editId) await api.put(`/clients/${editId}`, payload);
      else await api.post("/clients", payload);
      toast.success("Cliente salvato"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/clients/${id}`); toast.success("Eliminato"); load(); };
  const dlListino = async (c) => {
    try { await downloadFile(`/reports/listino-cliente/${c.id}.pdf`, `listino_${c.name}.pdf`); } catch { toast.error("Download non riuscito"); }
  };
  const openHistory = async (c) => {
    const { data } = await api.get(`/clients/${c.id}/history`);
    setHistory(data); setHistOpen(true);
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => openForm(null)} data-testid="add-client-btn" className="bg-[#0F4C81] text-white rounded-full">
              <Plus className="w-4 h-4 mr-2" />Nuovo cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Modifica" : "Nuovo"} cliente</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nome / Insegna *</Label><Input data-testid="client-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Tipo</Label><Input value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} placeholder="Bar, Lido…" className="mt-1" /></div>
                <div><Label>Città</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Referente</Label><Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="mt-1" /></div>
                <div><Label>Telefono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
              </div>
              <div><Label>Email (per promemoria vuoto a rendere)</Label><Input type="email" data-testid="client-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
              <div><Label>Sconto concordato %</Label><Input type="number" step="0.5" data-testid="client-discount-input" value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: e.target.value })} className="mt-1" /></div>
              <div><Label>Note</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" /></div>
            </div>
            <DialogFooter><Button onClick={save} data-testid="save-client-btn" className="bg-[#0F4C81] text-white rounded-full">Salva</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-2xl border border-stone-200 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Cliente</TableHead><TableHead>Tipo</TableHead><TableHead>Città</TableHead><TableHead>Referente</TableHead><TableHead>Azioni</TableHead></TableRow></TableHeader>
          <TableBody>
            {clients.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-stone-400 py-8">Nessun cliente. Aggiungine uno.</TableCell></TableRow>}
            {clients.map((c) => (
              <TableRow key={c.id} data-testid={`client-row-${c.id}`}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell>{c.type || "—"}</TableCell>
                <TableCell>{c.city || "—"}</TableCell>
                <TableCell className="text-stone-500">{c.contact || "—"}<div className="text-xs text-stone-400">{c.phone}</div></TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <button onClick={() => dlListino(c)} data-testid={`client-listino-${c.id}`} title="Listino personalizzato"><FileDown className="w-4 h-4 text-[#C2593F]" /></button>
                    <button onClick={() => openHistory(c)} data-testid={`client-history-${c.id}`} title="Storico"><History className="w-4 h-4 text-[#0F4C81]" /></button>
                    <button onClick={() => openForm(c)} data-testid={`edit-client-${c.id}`}><Pencil className="w-4 h-4 text-stone-500 hover:text-[#0F4C81]" /></button>
                    <button onClick={() => remove(c.id)} data-testid={`delete-client-${c.id}`}><Trash2 className="w-4 h-4 text-red-500" /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={histOpen} onOpenChange={setHistOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{history?.client?.name}</DialogTitle></DialogHeader>
          {history && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#EFF6FF] rounded-xl p-4"><div className="text-xs text-stone-500">Venduto totale</div><div className="font-mono font-bold text-xl text-[#0F4C81]">{eur(history.total_sales)}</div></div>
                <div className="bg-[#DCFCE7] rounded-xl p-4"><div className="text-xs text-stone-500">Provvigioni</div><div className="font-mono font-bold text-xl text-[#15803D]">{eur(history.total_commissions)}</div></div>
              </div>
              <div>
                <div className="flex items-center gap-2 font-semibold text-[#0B2A45] mb-2"><ShoppingCart className="w-4 h-4" /> Ordini ({history.orders.length})</div>
                {history.orders.length === 0 ? <p className="text-sm text-stone-400">Nessun ordine</p> : (
                  <div className="border rounded-xl overflow-hidden">
                    <Table><TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Articoli</TableHead><TableHead>Totale</TableHead><TableHead>Stato</TableHead></TableRow></TableHeader>
                      <TableBody>{history.orders.map((o) => (<TableRow key={o.id}><TableCell className="text-xs">{new Date(o.created_at).toLocaleDateString("it-IT")}</TableCell><TableCell>{o.items.length}</TableCell><TableCell className="font-mono">{eur(o.total)}</TableCell><TableCell className="text-xs">{o.status}</TableCell></TableRow>))}</TableBody>
                    </Table>
                  </div>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2 font-semibold text-[#0B2A45] mb-2"><Wrench className="w-4 h-4" /> Comodati ({history.comodati.length})</div>
                {history.comodati.length === 0 ? <p className="text-sm text-stone-400">Nessun comodato</p> : (
                  <div className="border rounded-xl overflow-hidden">
                    <Table><TableHeader><TableRow><TableHead>Impianto</TableHead><TableHead>Fusti/mese</TableHead><TableHead>Ammort.</TableHead><TableHead>Stato</TableHead></TableRow></TableHeader>
                      <TableBody>{history.comodati.map((c) => (<TableRow key={c.id}><TableCell>{c.machine_name}</TableCell><TableCell>{c.monthly_kegs}</TableCell><TableCell>{c.breakeven_months} mesi</TableCell><TableCell className="text-xs">{c.status}</TableCell></TableRow>))}</TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
